import {inject, singleton} from "tsyringe";
import {html} from "lit";
import {EventBus} from "./event-bus.service";
import {UiHelper} from "../helpers/ui.helper";
import {NOTIFICATION_DURATION} from "../constants";
import {IssuePersonalData} from "../domain/entities/issue-personal-data.entity";
import {CommandSendPayload} from "../message-broker/payloads/command-send.payload";
import {CommandName} from "../commands/command-name.enum";

/**
 * "My Organizer" notifications (Anke, 2026-09-06: "Es fehlen die
 * Notifications, dass das Issue der Spalte X hinzugefügt wurde oder wieder
 * aus der Box entfernt wurde") — surfaced as a REACTION to the business
 * event (a successfully sent Assign/Unassign/Move command), not as an inline
 * side effect of the drag&drop handler itself
 * (`<my-organizer-label-column>`/`<my-organizer-assigned-column>`). Mirrors
 * `GlobalNotificationService`'s own architecture (a decoupled listener
 * reacting to Command lifecycle, not notification code scattered across
 * every call site).
 *
 * Reworked 2026-09-06 ("My Organizer" reorder step, review-fund) to key off the
 * COMMAND TYPE (`assign`/`unassign`/`move`) rather than inferring intent from
 * `personalLabel`'s truthy/falsy value on the resulting entity — the previous
 * approach would have misfired a "Zugewiesen" toast for a future, purely
 * `timeCluster`-only update, since it had no way to tell "label changed" from
 * "label happens to still be set". The command type itself now carries that
 * intent unambiguously. `ReorderIssueOfPersonalLabelCommand` is deliberately
 * NOT subscribed here — a pure position change within a column is not a
 * notification-worthy business event (ADR-014).
 *
 * Registered once at app startup, see `services/index.ts`.
 */
@singleton()
export class MyOrganizerNotificationService {

    public constructor(
        @inject(EventBus) private readonly _eventBus: EventBus
    ) {
        this._eventBus.listenForCommandSendByTags(
            ['entity', 'assign', 'issue-personal-data'],
            ['entity', 'unassign', 'issue-personal-data'],
            ['entity', 'move', 'issue-personal-data'],
        ).subscribe(event => this._onIssuePersonalDataCommandSend(event.payload));
    }

    /**
     * Runs for BOTH outcomes, not just success — `command.payload` (the
     * entity as constructed/mutated before dispatch) still reflects what was
     * ATTEMPTED even when `dispatchCommand()` came back `false`.
     */
    private async _onIssuePersonalDataCommandSend(payload: CommandSendPayload<IssuePersonalData> | null): Promise<void> {
        if (!payload) {
            return;
        }

        const row = payload.command.payload;
        const issue = await row.issue;
        const issueTitle = issue?.title || '-';

        switch (payload.command.name) {
            case CommandName.ASSIGN_ISSUE_TO_PERSONAL_LABEL:
            case CommandName.MOVE_ISSUE_TO_ANOTHER_PERSONAL_LABEL: {
                const label = await row.personalLabel;
                const labelText = label?.label ?? '-';
                UiHelper.notify(payload.success
                    ? {
                        level: 'success',
                        title: 'Zugewiesen',
                        content: html`Issue «${issueTitle}» wurde «${labelText}» hinzugefügt.`,
                        closable: true,
                        duration: NOTIFICATION_DURATION,
                    }
                    : {
                        level: 'error',
                        title: 'Zuweisen fehlgeschlagen',
                        content: html`Issue «${issueTitle}» konnte nicht «${labelText}» hinzugefügt werden.`,
                        closable: true,
                        duration: NOTIFICATION_DURATION,
                    });
                return;
            }
            case CommandName.UNASSIGN_ISSUE_FROM_PERSONAL_LABEL: {
                // Doesn't name the label it was removed FROM — by the time this
                // listener sees the command, `personalLabel` is already `null`
                // on the entity, and the previous value isn't tracked anywhere
                // this listener can reach. Acceptable for Schritt 1.
                UiHelper.notify(payload.success
                    ? {
                        level: 'success',
                        title: 'Entfernt',
                        content: html`Issue «${issueTitle}» wurde entfernt.`,
                        closable: true,
                        duration: NOTIFICATION_DURATION,
                    }
                    : {
                        level: 'error',
                        title: 'Entfernen fehlgeschlagen',
                        content: html`Issue «${issueTitle}» konnte nicht entfernt werden.`,
                        closable: true,
                        duration: NOTIFICATION_DURATION,
                    });
                return;
            }
            default:
                // listenForCommandSendByTags() already filters to the three
                // subscribed commands above — unreachable in practice, kept as
                // a defensive no-op rather than a silent wrong toast.
                return;
        }
    }

}
